/**
 * __FLEET_TRACKING__ Google Maps for the Fleet Tracking view, through the board's shared loader.
 *
 * Prefers the board-wide helper services/mapsKeyService.js (ensureGoogleMaps: the shared key from
 * the database, then the routing-config key, then mapService.loadGoogleMapsScript) as soon as that
 * file exists; until then it uses the Route Builder's own path (routing-config key ->
 * mapService.loadGoogleMapsScript). Either way the script is injected once, by the same loader.
 * Never sets window.gm_authFailure (the shared loader owns it).
 * Rejects with an Error whose .code is 'NO_KEY' | 'AUTH_FAILED' | 'LOAD_FAILED'.
 */
import { getRoutingConfig } from './routingEngineService';
import { loadGoogleMapsScript } from './mapService';

// require.context resolves at build time and is simply empty while mapsKeyService.js is absent,
// so this module builds with or without it (no hard import of a file that may not exist yet).
let shared = null;
try {
  // eslint-disable-next-line no-undef
  const ctx = require.context('./', false, /^\.\/mapsKeyService\.js$/);
  if (ctx.keys().length) shared = ctx(ctx.keys()[0]);
} catch (e) {
  shared = null;
}

const coded = (e, fallback) => {
  const err = new Error((e && e.message) || fallback);
  err.code = (e && e.code) || fallback;
  return err;
};

export const ensureFleetMaps = async () => {
  if (window.google && window.google.maps && window.google.maps.Map) return window.google.maps;
  if (shared && typeof shared.ensureGoogleMaps === 'function') {
    try { return await shared.ensureGoogleMaps(); } catch (e) { throw coded(e, 'LOAD_FAILED'); }
  }
  let key = '';
  try { key = (getRoutingConfig() || {}).googleMapsApiKey || ''; } catch (e) { key = ''; }
  if (!key) throw coded({ message: 'No Google Maps browser key is set for the board.', code: 'NO_KEY' }, 'NO_KEY');
  try {
    return await loadGoogleMapsScript(key);
  } catch (e) {
    throw coded({ message: (e && e.message) || 'Google Maps failed to load', code: 'LOAD_FAILED' }, 'LOAD_FAILED');
  }
};

/** Subscribe to "Google refused the key" (shared loader only); returns an unsubscribe function. */
export const onFleetMapsAuthFailure = (cb) => {
  if (shared && typeof shared.onMapsAuthFailure === 'function') {
    try { return shared.onMapsAuthFailure(cb) || (() => {}); } catch (e) { return () => {}; }
  }
  return () => {};
};

export default ensureFleetMaps;
